import { createContext, useContext, useState, type ReactNode } from "react";

//
// TYPES
//
type DialogType = "confirm" | "alert" | "deleteUser";

interface DialogOptions {
  type: DialogType;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string; // confirm/delete mode only
}

interface ConfirmContextValue {
  confirm: (options: Omit<DialogOptions, "type">) => Promise<boolean>;
  alert: (
    options: Omit<DialogOptions, "type" | "confirmText">,
  ) => Promise<void>;
  deleteUserConfirm: (options: Omit<DialogOptions, "type">) => Promise<boolean>;
}

//
// CONTEXT
//
const ConfirmContext = createContext<ConfirmContextValue | undefined>(
  undefined,
);

export const ConfirmDialogProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [options, setOptions] = useState<DialogOptions | null>(null);
  const [resolver, setResolver] = useState<((value: any) => void) | null>(null);

  const [deleteInput, setDeleteInput] = useState("");

  //
  // FUNCTIONS EXPOSED TO THE APP
  //

  const confirm = (opts: Omit<DialogOptions, "type">): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setOptions({ ...opts, type: "confirm" });
      setResolver(() => resolve);
    });
  };

  const alert = (
    opts: Omit<DialogOptions, "type" | "confirmText">,
  ): Promise<void> => {
    return new Promise<void>((resolve) => {
      setOptions({ ...opts, type: "alert", confirmText: "OK" });
      setResolver(() => resolve);
    });
  };

  const deleteUserConfirm = (
    opts: Omit<DialogOptions, "type">,
  ): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setOptions({ ...opts, type: "deleteUser", confirmText: "Slett" });
      setResolver(() => resolve);
    });
  };

  //
  // CLOSE HANDLER
  //
  const handleClose = (value: boolean | void) => {
    resolver?.(value);
    setOptions(null);
    setResolver(null);
    setDeleteInput(""); // reset delete text
  };

  //
  // RENDER
  //
  return (
    <ConfirmContext.Provider value={{ confirm, alert, deleteUserConfirm }}>
      {children}

      {options && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-sm animate-in fade-in zoom-in">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              {options.title ||
                (options.type === "alert" ? "Melding" : "Bekreft")}
            </h2>

            <p className="text-gray-600 whitespace-pre-line mb-4">
              {options.message}
            </p>

            {/* === DELETE USER SPECIAL INPUT === */}
            {options.type === "deleteUser" && (
              <div className="mb-6">
                <input
                  type="text"
                  value={deleteInput}
                  onChange={(e) => setDeleteInput(e.target.value)}
                  placeholder="Skriv SLETT for å bekrefte"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-400"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Du må skrive{" "}
                  <span className="font-bold text-red-600">SLETT</span> for å
                  slette brukeren.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              {/* Show cancel only for confirm/delete */}
              {(options.type === "confirm" ||
                options.type === "deleteUser") && (
                <button
                  onClick={() => handleClose(false)}
                  className="px-4 py-2 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300"
                >
                  {options.cancelText || "Avbryt"}
                </button>
              )}

              {/* Confirm button */}
              <button
                disabled={
                  options.type === "deleteUser" &&
                  deleteInput.trim() !== "SLETT"
                }
                onClick={() => handleClose(true)}
                className={`px-4 py-2 rounded-md text-white 
                  ${
                    options.type === "alert"
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-red-600 hover:bg-red-700"
                  }
                  ${
                    options.type === "deleteUser" &&
                    deleteInput.trim() !== "SLETT"
                      ? "opacity-40 cursor-not-allowed"
                      : ""
                  }
                `}
              >
                {options.confirmText ||
                  (options.type === "alert" ? "OK" : "Ja")}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

//
// HOOKS
//
export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx)
    throw new Error("useConfirm must be used inside ConfirmDialogProvider");
  return ctx.confirm;
};

export const useAlert = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx)
    throw new Error("useAlert must be used inside ConfirmDialogProvider");
  return ctx.alert;
};

export const useDeleteUserConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx)
    throw new Error(
      "useDeleteUserConfirm must be used inside ConfirmDialogProvider",
    );
  return ctx.deleteUserConfirm;
};
